import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Home, DollarSign, ShieldCheck, Clock, ArrowRight,
  ChevronDown, Sparkles, Calendar, Star,
} from "lucide-react";
import type { RootState } from "../../redux/store";

/* ─────────────────────────────────────────────────────────
   HEARTLAS DESIGN TOKENS — same palette as SmartFooter/AtlasHome
───────────────────────────────────────────────────────── */
const HEART = {
  bg: "#FBF6EE",
  panelBg: "#FFFFFF",

  dark: "#1B0E06",
  textMuted: "#7A6653",
  textFaint: "#A6947F",

  red: "#C8202E",
  redDark: "#9E1521",
  redSoft: "#FBE7E9",

  gold: "#F2A93E",
  goldDark: "#D4900F",
  goldSoft: "#FCEFD4",

  border: "#EEDFC4",
};

const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;600;700;800;900&family=DM+Sans:wght@400;500;600;700&display=swap');
  .hp-root *, .hp-root *::before, .hp-root *::after { box-sizing: border-box; }
  .hp-root { font-family:'DM Sans',sans-serif; background:${HEART.bg}; color:${HEART.dark}; }

  @keyframes hpFloat { 0%,100%{transform:translateY(0);} 50%{transform:translateY(-8px);} }
  @keyframes hpPop { from{opacity:0;transform:translateY(10px);} to{opacity:1;transform:translateY(0);} }

  .hp-hero-emoji{ animation:hpFloat 3.2s ease-in-out infinite; display:inline-block; }

  .hp-cta{
    display:inline-flex;align-items:center;gap:10px;
    background:linear-gradient(135deg, ${HEART.red}, ${HEART.redDark});
    color:#fff;border:none;border-radius:14px;
    padding:16px 30px;font-family:'Sora',sans-serif;font-size:16px;font-weight:800;
    cursor:pointer;transition:transform .18s, box-shadow .2s;
    box-shadow:0 10px 24px rgba(200,32,46,.32);
  }
  .hp-cta:hover{ transform:translateY(-2px); box-shadow:0 14px 30px rgba(200,32,46,.4); }

  .hp-perk-card{
    background:${HEART.panelBg}; border:1.5px solid ${HEART.border}; border-radius:18px;
    padding:22px 20px; display:flex; flex-direction:column; gap:10px;
    animation:hpPop .35s ease both; transition:transform .15s, box-shadow .15s, border-color .15s;
  }
  .hp-perk-card:hover{ transform:translateY(-3px); box-shadow:0 12px 26px rgba(27,14,6,0.08); border-color:${HEART.gold}; }

  .hp-perk-icon{
    width:46px;height:46px;border-radius:50%;
    background:${HEART.goldSoft}; color:${HEART.red};
    display:flex;align-items:center;justify-content:center;flex-shrink:0;
  }

  .hp-faq-item{
    border:1.5px solid ${HEART.border}; border-radius:14px; overflow:hidden;
    background:${HEART.panelBg};
  }
  .hp-faq-q{
    width:100%; display:flex; align-items:center; justify-content:space-between; gap:12px;
    background:transparent; border:none; cursor:pointer; text-align:left;
    padding:16px 18px; font-family:'Sora',sans-serif; font-weight:700; font-size:14.5px; color:${HEART.dark};
  }
  .hp-faq-chevron{ transition:transform .2s; flex-shrink:0; color:${HEART.red}; }
  .hp-faq-chevron.open{ transform:rotate(180deg); }
  .hp-faq-a{
    padding:0 18px; max-height:0; overflow:hidden; transition:max-height .25s ease, padding .25s ease;
    font-size:13.5px; color:${HEART.textMuted}; line-height:1.6;
  }
  .hp-faq-a.open{ padding:0 18px 18px; max-height:300px; }

  .hp-footlink{ color:${HEART.textMuted}; text-decoration:none; font-size:13px; font-weight:500; cursor:pointer; }
  .hp-footlink:hover{ color:${HEART.red}; }

  @media(max-width:760px){
    .hp-wrap{ padding:32px 18px 56px !important; }
    .hp-hero-title{ font-size:26px !important; }
    .hp-perk-grid{ grid-template-columns:1fr !important; }
  }
`;

/* ─────────────────────────────────────────────────────────
   FAQ DATA
───────────────────────────────────────────────────────── */
const FAQS: { q: string; a: string }[] = [
  {
    q: "How much does it cost to list?",
    a: "Listing is completely free. Heartlas only takes a small service fee once you actually get a booking — no upfront costs, no monthly fees.",
  },
  {
    q: "How fast can I start hosting?",
    a: "Most hosts finish setup in under 10 minutes. Once you submit your details, your place can go live right away and you can start accepting bookings the same day.",
  },
  {
    q: "When do I get paid?",
    a: "Payouts are sent automatically after each guest checks in, straight to your linked bank account or card — no chasing invoices.",
  },
  {
    q: "What if I need to make changes later?",
    a: "You're always in control. You can update your pricing, photos, availability, or house rules anytime from your host dashboard.",
  },
  {
    q: "Is my property protected?",
    a: "Yes. Every booking is covered by our host protection policies and guests are verified before they can book, so you can host with peace of mind.",
  },
];

/* ─────────────────────────────────────────────────────────
   MAIN COMPONENT
───────────────────────────────────────────────────────── */
const Hosting: React.FC = () => {
  const navigate = useNavigate();
  const user = useSelector((s: RootState) => s.auth.user);
  const [openFaq, setOpenFaq] = useState<number | null>(0);

  const handleGetStarted = () => {
    if (user?.userId) navigate(`/commerceonboarding/${user.userId}`);
    else navigate("/authswitch");
  };

  const perks = [
    { icon: DollarSign, title: "Earn extra income", body: "Turn your space into steady income — you set the price, we help fill the calendar." },
    { icon: Clock, title: "Set up in minutes", body: "A few photos, a few details, and you're live. No paperwork headaches." },
    { icon: ShieldCheck, title: "Hosting protection", body: "Verified guests and built-in protections mean fewer surprises." },
    { icon: Calendar, title: "Full control", body: "You decide the price, the dates, and the house rules — always." },
  ];

  return (
    <div className="hp-root" style={{ minHeight: "100vh" }}>
      <style>{CSS}</style>

      <div className="hp-wrap" style={{ maxWidth: 860, margin: "0 auto", padding: "56px 24px 72px" }}>
        {/* ── HERO ── */}
        <div style={{ textAlign: "center", marginBottom: 48 }}>
          <div className="hp-hero-emoji" style={{ fontSize: 56, marginBottom: 14 }}>🏡</div>
          <h1
            className="hp-hero-title"
            style={{
              fontFamily: "'Sora',sans-serif", fontWeight: 900, fontSize: 34,
              letterSpacing: "-0.02em", margin: "0 0 12px", color: HEART.dark,
            }}
          >
            Share your space,<br />start earning on Heartlas
          </h1>
          <p style={{ fontSize: 15, color: HEART.textMuted, lineHeight: 1.7, maxWidth: 480, margin: "0 auto 28px" }}>
            List your home, room, or property in minutes and welcome guests from
            over 100+ countries. It's free to list, and you're always in control.
          </p>
          <button className="hp-cta" onClick={handleGetStarted}>
            <Home size={18} /> Get Started <ArrowRight size={16} />
          </button>

          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, marginTop: 18 }}>
            <div style={{ display: "flex" }}>
              {[1, 2, 3, 4, 5].map((n) => (
                <Star key={n} size={14} color={HEART.gold} fill={HEART.gold} strokeWidth={0} />
              ))}
            </div>
            <span style={{ fontSize: 12.5, color: HEART.textFaint, fontWeight: 600 }}>
              Loved by thousands of hosts
            </span>
          </div>
        </div>

        {/* ── PERKS ── */}
        <div
          className="hp-perk-grid"
          style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 52 }}
        >
          {perks.map(({ icon: Icon, title, body }, i) => (
            <div key={title} className="hp-perk-card" style={{ animationDelay: `${i * 60}ms` }}>
              <span className="hp-perk-icon">
                <Icon size={20} />
              </span>
              <p style={{ fontFamily: "'Sora',sans-serif", fontWeight: 800, fontSize: 15, margin: 0 }}>{title}</p>
              <p style={{ fontSize: 13, color: HEART.textMuted, lineHeight: 1.6, margin: 0 }}>{body}</p>
            </div>
          ))}
        </div>

        {/* ── FAQ ── */}
        <div style={{ marginBottom: 44 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 18, justifyContent: "center" }}>
            <Sparkles size={16} color={HEART.red} />
            <h2 style={{ fontFamily: "'Sora',sans-serif", fontWeight: 800, fontSize: 20, margin: 0 }}>
              Questions, answered
            </h2>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {FAQS.map((item, i) => {
              const open = openFaq === i;
              return (
                <div key={item.q} className="hp-faq-item">
                  <button className="hp-faq-q" onClick={() => setOpenFaq(open ? null : i)}>
                    {item.q}
                    <ChevronDown size={18} className={`hp-faq-chevron${open ? " open" : ""}`} />
                  </button>
                  <div className={`hp-faq-a${open ? " open" : ""}`}>{item.a}</div>
                </div>
              );
            })}
          </div>
        </div>

        {/* ── BOTTOM CTA ── */}
        <div
          style={{
            textAlign: "center", background: HEART.panelBg, border: `1.5px solid ${HEART.border}`,
            borderRadius: 20, padding: "32px 24px",
          }}
        >
          <p style={{ fontFamily: "'Sora',sans-serif", fontWeight: 800, fontSize: 18, margin: "0 0 8px" }}>
            Ready to list your place?
          </p>
          <p style={{ fontSize: 13.5, color: HEART.textMuted, margin: "0 0 20px" }}>
            It only takes a few minutes to get started.
          </p>
          <button className="hp-cta" onClick={handleGetStarted}>
            <Home size={18} /> List Your Property <ArrowRight size={16} />
          </button>
        </div>

        {/* ── FOOT LINKS (relative paths, no hardcoded domain) ── */}
        <div
          style={{
            display: "flex", justifyContent: "center", gap: 20, flexWrap: "wrap",
            marginTop: 40, paddingTop: 24, borderTop: `1px solid ${HEART.border}`,
          }}
        >
          <span className="hp-footlink" onClick={() => navigate("/pages/terms")}>Terms</span>
          <span className="hp-footlink" onClick={() => navigate("/pages/privacy")}>Privacy</span>
          <span className="hp-footlink" onClick={() => navigate("/pages/host-guidelines")}>Host Guidelines</span>
          <span className="hp-footlink" onClick={() => navigate("/support")}>Help Center</span>
        </div>
      </div>
    </div>
  );
};

export default Hosting;
